// src/containers/Trader_Form/botLive.js
//
// The live-bot half of Trader_Form, as pure functions (UI build unit T1).
//
// Everything here was lifted verbatim out of `Trader_Form.js` so the redesigned
// AutoTrader page (`src/pages/AutoTrader/`) reads a bot exactly the way the old
// page does — same row shape from GET /api/trading_bots, same socket merge, same
// history paging and the same "why isn't it trading" vocabulary — without
// copying 13k lines' worth of closure. Trader_Form imports these back, so there
// is one implementation and the two pages cannot drift.
//
// No React, no services, no MUI: each function takes plain data and returns
// plain data. The MUI icons Trader_Form draws beside a trading status are added
// by its own thin wrapper (`getTradingStatusDisplay`), not here.

import { futuresBlockFromSocket } from '../../components/Bot/botStatus';

/* ── Trade history (user_history rows) ─────────────────────────────────────── */

/** One page of GET /api/userhistory/by_bot/:botId. */
export const HISTORY_PAGE_SIZE = 50;

// Convert raw user_history rows ({ date, buy_sell_history }) into the
// { _ts, timestamp, text } entries the bot history table consumes. `_ts` is a
// sortable epoch so older entries paged in later merge into the right order.
export function mapHistoryRows(rows = []) {
  return (Array.isArray(rows) ? rows : []).map((entry) => {
    let text = entry.buy_sell_history;
    try { text = JSON.parse(text); } catch (_) {}
    const ms = new Date(entry.date).getTime();
    return {
      _ts: Number.isFinite(ms) ? ms : 0,
      timestamp: new Date(entry.date).toLocaleString(),
      text,
    };
  });
}

// Merge new history entries into an existing per-bot array: dedup by the
// composite key and keep oldest-first order (the table reverses for display).
export function historyKeyOf(e) {
  const t = (e && e.text) || {};
  return `${e.timestamp}|${t.side}|${t.price}|${t.quantity}|${t.symbol}`;
}
export function mergeHistoryEntries(existing = [], incoming = []) {
  const byKey = new Map();
  for (const e of existing) byKey.set(historyKeyOf(e), e);
  for (const e of incoming) { const k = historyKeyOf(e); if (!byKey.has(k)) byKey.set(k, e); }
  return Array.from(byKey.values()).sort((a, b) => (a._ts || 0) - (b._ts || 0));
}

export function computeStatsFromHistory(historyEntries = []) {
  if (!Array.isArray(historyEntries) || historyEntries.length === 0) return null;

  const sorted = historyEntries
    .map(e => ({ ts: new Date(e.timestamp).getTime(), t: e.text || {} }))
    .filter(e =>
      Number.isFinite(e.ts) &&
      e.t &&
      e.t.side &&
      e.t.type !== 'order_pending' &&
      e.t.type !== 'order_cancelled' &&
      e.t.type !== 'order_expired' &&
      e.t.type !== 'reconciliation'
    )
    .sort((a, b) => a.ts - b.ts);

  const buyQueue = []; // FIFO: { qty, costPerUnit, feePerUnit }
  let trades = 0, wins = 0, losses = 0;
  let grossProfit = 0, grossLoss = 0;
  let openQty = 0, openCost = 0;

  for (const { t } of sorted) {
    const side = String(t.side).toUpperCase();
    const qty = Number(t.quantity) || 0;
    const cost = Number(t.cost) || 0;
    const proceeds = Number(t.proceeds) || 0;
    const fee = Number(t.fee) || 0;
    if (qty <= 0) continue;

    if (side === 'BUY') {
      buyQueue.push({
        qty,
        costPerUnit: cost / qty,
        feePerUnit: fee / qty,
      });
      openQty += qty;
      openCost += cost;
    } else if (side === 'SELL') {
      let remaining = qty;
      // `proceeds` in buy_sell_history is proceedsAfterFees — ALREADY net of the
      // sell-side fee (see tradeExecutors.computeProceeds / historyPayload). So we
      // must NOT subtract the sell `fee` again here; the original `pnl -= fee`
      // double-counted it. The buy-side `cost`/costPerUnit is gross notional, so
      // the buy-side feePerUnit IS still subtracted to get a true net P&L.
      const proceedsPerUnit = proceeds / qty;
      let pnl = 0;
      let matchedQty = 0;

      while (remaining > 1e-12 && buyQueue.length > 0) {
        const head = buyQueue[0];
        const m = Math.min(remaining, head.qty);
        // Net proceeds per unit minus gross cost minus the prorated buy-side fee.
        pnl += m * (proceedsPerUnit - head.costPerUnit - head.feePerUnit);
        head.qty -= m;
        remaining -= m;
        openQty -= m;
        openCost -= m * head.costPerUnit;
        matchedQty += m;
        if (head.qty <= 1e-12) buyQueue.shift();
      }

      // Only count the trade for the portion that actually closed a position;
      // an unmatched sell (no open long) isn't charged a fee or counted.
      if (matchedQty > 1e-12) {
        trades += 1;
        if (pnl >= 0) { wins += 1; grossProfit += pnl; }
        else { losses += 1; grossLoss += Math.abs(pnl); }
      }
    }
  }

  return {
    Trades: trades,
    Wins: wins,
    Losses: losses,
    WinRatePct: trades > 0 ? (wins / trades) * 100 : 0,
    GrossProfit: grossProfit,
    GrossLoss: grossLoss,
    ProfitFactor: grossLoss > 0
      ? grossProfit / grossLoss
      : (grossProfit > 0 ? Infinity : 0),
    RealizedPnL: grossProfit - grossLoss,
    OpenPositionQty: openQty,
    OpenPositionCost: openCost,
  };
}

/* ── TradingView webhook URL ──────────────────────────────────────────────── */

export const WEBHOOK_BASE_URL = "https://trade.cryptologics-ai.com"; // ✅ Force production webhook domain

export const TV_WEBHOOK_PATH = "/api/webhooks/tradingview";

// botId can be "BOT_ID" for template usage in UI
export const buildTradingViewWebhookUrl = (botId, secret) => {
  const s = (secret || "").trim();
  const qs = new URLSearchParams();
  qs.set("bot_id", String(botId));
  if (s) qs.set("secret", s);
  return `${WEBHOOK_BASE_URL}${TV_WEBHOOK_PATH}?${qs.toString()}`;
};

/* ── Bot statistics ───────────────────────────────────────────────────────── */

/** The server's camelCase `statistics` → the panel's PascalCase shape. */
export function normalizeBotStats(raw = {}) {
  if (!raw || typeof raw !== 'object') return {};

  const num = (v) => {
    if (v === null || v === undefined || v === '') return undefined;
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  };

  return {
    // Core capital
    InitialCapital: num(raw.initialCapital),
    CashQuote: num(raw.cashQuote),
    Equity: num(raw.equity),
    PeakEquity: num(raw.peakEquity),

    // Long position info
    PositionQty: num(raw.positionQty),
    PositionCost: num(raw.positionCost),
    EntryPrice: num(raw.entryPrice),
    MarkPrice: num(raw.markPrice),
    TrailPeakPrice: num(raw.trailPeakPrice),

    // ─── Short position info (Phase 7) ───
    ShortPositionQty: num(raw.shortPositionQty),
    ShortPositionCost: num(raw.shortPositionCost),
    ShortEntryPrice: num(raw.shortEntryPrice),
    ShortTrailLowPrice: num(raw.shortTrailLowPrice),

    // PnL
    RealizedPnL: num(raw.realizedPnL),
    RealizedPnLPct: num(raw.realizedPnLPct),
    GrossProfit: num(raw.grossProfit),
    GrossLoss: num(raw.grossLoss),

    // Drawdown
    MaxDrawdownPct: num(raw.maxDrawdownPct),

    // Trade stats
    Trades: num(raw.trades),
    Wins: num(raw.wins),
    Losses: num(raw.losses),
    WinRatePct: num(raw.winRatePct),
    // The engine emits 9999 as a "wins, no losses" sentinel; show it as ∞
    // (the panel's fmtRatio renders Infinity as '∞').
    ProfitFactor: (() => { const pf = num(raw.profitFactor); return pf === 9999 ? Infinity : pf; })(),

    // Volume & Fees
    VolumeBase: num(raw.volumeBase),
    VolumeQuote: num(raw.volumeQuote),
    FeesPaidQuote: num(raw.feesPaidQuote),

    // Meta
    Initialized: num(raw.initialized),
    UpdatedAt: raw.updatedAt,

    __raw: raw,
  };
}

/* ── One bot row from GET /api/trading_bots ───────────────────────────────── */

/** The shape every bot on the page is kept in (was the `refreshBots` map). */
export function formatBotRow(bot) {
  let tradeInfo = {};
  try {
    tradeInfo = JSON.parse(bot.trade_info || '{}');
  } catch (_) { }

  const webhookSecret = (tradeInfo.webhook?.secret || '').trim();
  const signalSource = tradeInfo.signalSource || 'logic';

  return {
    id: bot.id,
    name: bot.name,
    symbol: tradeInfo.symbol,
    interval: tradeInfo.timeframe,
    exchangeId: bot.exchange_id ?? bot.exchangeId ?? null,
    exchange: bot.exchange?.exchange || '',
    trade_info: bot.trade_info,
    // The COLUMN, carried through alongside trade_info. Stock-only UI
    // reads `ti.asset_class || bot.asset_class`, and that second half
    // could never fire because this was dropped here — so a stock bot
    // whose asset class lives only in the column (anything created
    // before trade_info started mirroring it) read as crypto, and the
    // stock-only controls silently did not render.
    asset_class: bot.asset_class ?? null,
    exchangeName: bot.exchange?.name || bot.exchange?.exchange || "—",
    status: bot.stop_trade_ASSET ? 'paused' : 'active',
    stop_trade_ASSET: bot.stop_trade_ASSET,
    logic: {
      buy: bot.buy_logic,
      sell: bot.sell_logic,
      shortOpen: tradeInfo.futures?.shortOpenLogic || tradeInfo.short_open_logic || bot.short_open_logic || '',
      shortClose: tradeInfo.futures?.shortCloseLogic || tradeInfo.short_close_logic || bot.short_close_logic || '',
      profitStop: tradeInfo.profitStop,
      lossStop: tradeInfo.lossStop,
      maxLoss: tradeInfo.maxDailyLoss,
      maxTrades: tradeInfo.maxTradesPerDay,
      trailingStop: tradeInfo.trailingStop,
      investment: tradeInfo.totalInvestment,
    },
    // Normalize server stats at fetch time (the socket path already does)
    // so bot.stats is always the panel's PascalCase shape; otherwise the
    // first paint would read camelCase DB stats and show blanks.
    stats: bot.statistics ? normalizeBotStats(bot.statistics) : undefined,
    statusMessage: bot.status || '',
    errorMessage: bot.error_message || '',
    // The engine's live futures refusal (api futures_block, read from
    // botStats:{id}): { code, reason, at } or null. See botStatus.futuresBlockOf.
    futures_block: bot.futures_block || null,

    // ✅ TradingView webhook fields
    signalSource,
    webhookUrl: signalSource === 'tradingview'
      ? buildTradingViewWebhookUrl(bot.id, webhookSecret)
      : '',
    webhookConfig: tradeInfo.webhook || null,
    isScannerProfile: bot.status === 'scanner_profile',
    spawnedFromProfileId: bot.spawned_from_profile_id ?? null,
    scannerConfig: tradeInfo.scanner || null,
    spawnedFromMeta: tradeInfo._spawned_from || null,
  };
}

/**
 * Scanner profiles first, then regular bots, deduplicated by id — guards
 * against any API call returning overlapping rows.
 */
export function mergeBotLists(scannerProfiles = [], regularBots = []) {
  const seenIds = new Set();
  const out = [];
  for (const bot of [...(scannerProfiles || []), ...(regularBots || [])]) {
    if (seenIds.has(bot.id)) continue;
    seenIds.add(bot.id);
    out.push(bot);
  }
  return out;
}

/* ── The `botUpdate:<id>` socket event ────────────────────────────────────── */

/** The event's stats block, normalized, or null when it carries none. */
export function socketStatsOf(data = {}) {
  // ✅ Handle stats - could be object, string, or nested in botData
  let statsData = data.stats || data.botData?.stats;
  if (typeof statsData === 'string') {
    try { statsData = JSON.parse(statsData); } catch { statsData = null; }
  }
  return statsData && Object.keys(statsData).length > 0
    ? normalizeBotStats(statsData)
    : null;
}

/** The event's `history` array as table entries ({ _ts, timestamp, text }). */
export function socketHistoryEntries(data = {}) {
  const historyData = Array.isArray(data.history) ? data.history : [];
  return historyData.map(entry => {
    let parsed = entry;
    if (typeof entry === 'string') {
      try { parsed = JSON.parse(entry); } catch { return null; }
    }
    let cleanText = parsed.buy_sell_history;
    if (typeof cleanText === 'string') {
      try { cleanText = JSON.parse(cleanText); } catch { }
    }
    const ms = new Date(parsed.date).getTime();
    return { _ts: Number.isFinite(ms) ? ms : 0, timestamp: new Date(parsed.date).toLocaleString(), text: cleanText };
  }).filter(Boolean);
}

/** Apply one `botUpdate` event to a bot row. Returns a NEW row. */
export function applyBotSocketUpdate(bot, data = {}, normalizedStats = null) {
  const updatedBot = { ...bot };

  // ✅ Update stats if present
  if (normalizedStats) {
    updatedBot.stats = normalizedStats;
  }

  // The engine's futures refusal rides on the full botStats hash it sends:
  // set or cleared from there, left alone when the event carries none.
  const block = futuresBlockFromSocket(data);
  if (block !== undefined) updatedBot.futures_block = block;

  // Status fields
  if (data.error_message !== undefined) updatedBot.errorMessage = data.error_message;
  if (data.error_side !== undefined) updatedBot.errorSide = data.error_side;
  if (data.error_code !== undefined) updatedBot.errorCode = data.error_code;
  if (data.error_at !== undefined) updatedBot.errorAt = data.error_at;
  if (data.status !== undefined) updatedBot.statusMessage = data.status;
  if (data.stop_trade_ASSET !== undefined) {
    updatedBot.stop_trade_ASSET = data.stop_trade_ASSET;
    updatedBot.status = data.stop_trade_ASSET ? 'paused' : 'active';
  }

  // Guard data from decision engine
  if (data.guard_tag !== undefined) updatedBot.guardTag = data.guard_tag || '';
  if (data.guard_reason !== undefined) updatedBot.guardReason = data.guard_reason || '';
  if (data.guard_at !== undefined) updatedBot.guardAt = data.guard_at || '';
  if (data.guard_side !== undefined) updatedBot.guardSide = data.guard_side || '';
  if (data.guard_price !== undefined) updatedBot.guardPrice = data.guard_price || '';

  // Current price
  if (data.ASSET_PRICE) updatedBot.currentPrice = data.ASSET_PRICE;

  // ✅ Last trade info
  if (data.last_trade_side) updatedBot.lastTradeSide = data.last_trade_side;
  if (data.last_trade_price) updatedBot.lastTradePrice = data.last_trade_price;
  if (data.last_trade_qty) updatedBot.lastTradeQty = data.last_trade_qty;
  if (data.last_trade_ts) updatedBot.lastTradeTs = data.last_trade_ts;

  // Pending limit order info
  if (data.pending_order_id !== undefined) updatedBot.pendingOrderId = data.pending_order_id || '';
  if (data.pending_order_side !== undefined) updatedBot.pendingOrderSide = data.pending_order_side || '';
  if (data.pending_order_price !== undefined) updatedBot.pendingOrderPrice = data.pending_order_price || '';
  // Phase A: partial fill progress
  if (data.pending_order_quantity !== undefined) updatedBot.pendingOrderQuantity = data.pending_order_quantity || '';
  if (data.pending_order_applied_qty !== undefined) updatedBot.pendingOrderAppliedQty = data.pending_order_applied_qty || '';
  // Legacy trading status fields
  if (data.trading_status !== undefined) updatedBot.tradingStatus = data.trading_status;
  if (data.trading_status_reason !== undefined) updatedBot.tradingStatusReason = data.trading_status_reason;

  // ✅ Clear guard state if status is active and no guard_tag
  if (data.status === 'active' && !data.guard_tag && !data.error_message) {
    updatedBot.guardTag = '';
    updatedBot.guardReason = '';
    updatedBot.guardAt = '';
    updatedBot.guardSide = '';
    updatedBot.guardPrice = '';
    updatedBot.errorMessage = '';
  }

  return updatedBot;
}

/* ── "Why isn't it trading" ───────────────────────────────────────────────── */

// Helper to format camelCase/snake_case to readable text
export function formatReasonLabel(str) {
  if (!str) return '';
  return str
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .replace(/^\s+/, '')
    .split(' ')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

const STATUS_CONFIG = {
  error: {
    color: 'error',
    reasons: {},  // dynamic — uses errorMessage directly
  },
  PAUSE: {
    color: 'warning',
    reasons: {
      profitStop: { label: 'Profit Target Reached', description: 'Take profit threshold hit. Trading paused.' },
      lossStop: { label: 'Stop Loss Triggered', description: 'Maximum loss threshold reached.' },
      maxDailyLoss: { label: 'Daily Loss Limit', description: 'Maximum daily loss reached. Resumes tomorrow.' },
      maxTradesPerDay: { label: 'Daily Trade Limit Reached', description: 'Maximum trades per day reached. Resumes at midnight UTC.' },
      manualStop: { label: 'Manually Paused', description: 'Bot was manually paused by user.' },
    }
  },
  FORCE_SELL: {
    color: 'error',
    reasons: {
      trailingStop: { label: 'Trailing Stop Hit', description: 'Price dropped below trailing stop. Position closed.' },
      lossStop: { label: 'Stop Loss Executed', description: 'Stop loss triggered. Position closed.' },
    }
  },
};

/**
 * The guard / error state the engine reported for a bot, or null for the
 * normal flow (IDLE / ALLOW / BLOCK, or nothing).
 *
 * `{ status, reason, color ('error'|'warning'), label, description, guardAt,
 *    guardPrice, guardSide }` — Trader_Form's `getTradingStatusDisplay` adds
 * its MUI icon to this; the new AutoTrader page renders it as its status line.
 */
export function tradingStatusOf(bot) {
  if (!bot) return null;
  let status = bot.guardTag || bot.tradingStatus;
  let reason = bot.guardReason || bot.tradingStatusReason;

  // ✅ BLOCK states are normal trading flow - don't show alerts
  if (status === 'BLOCK') {
    return null;
  }

  // Fallback: derive from errorMessage if no explicit status
  if (!status && bot.errorMessage) {
    const errLower = bot.errorMessage.toLowerCase();

    // Skip normal block reasons
    if (errLower === 'noposition' || errLower === 'alreadyinposition') {
      return null;
    }

    if (errLower.includes('maxtrades') || errLower.includes('maxtradesperday')) {
      status = 'PAUSE';
      reason = 'maxTradesPerDay';
    } else if (errLower.includes('profit') && !errLower.includes('factor')) {
      status = 'PAUSE';
      reason = 'profitStop';
    } else if (errLower.includes('dailyloss') || errLower.includes('daily loss')) {
      status = 'PAUSE';
      reason = 'maxDailyLoss';
    } else if (errLower.includes('loss') && !errLower.includes('daily')) {
      status = 'PAUSE';
      reason = 'lossStop';
    } else if (errLower.includes('trailing')) {
      status = 'FORCE_SELL';
      reason = 'trailingStop';
    } else if (errLower.includes('manual')) {
      status = 'PAUSE';
      reason = 'manualStop';
    }

    if (!status) {
      return {
        status: 'error',
        reason: 'tradeRejected',
        color: 'error',
        label: 'Trade Rejected',
        description: bot.errorMessage,    // shows the full reason
        guardAt: bot.errorAt,
        guardSide: bot.errorSide,
      };
    }
  }

  // No alert needed for normal states
  if (!status || status === 'IDLE' || status === 'ALLOW') {
    return null;
  }

  const config = STATUS_CONFIG[status];
  if (!config) return null; // Unknown status - don't show

  const reasonInfo = config.reasons[reason] || {
    label: status === 'error' ? 'Trade Error' : formatReasonLabel(reason || status),
    description: status === 'error'
      ? (bot.errorMessage || `Trading error: ${formatReasonLabel(reason)}`)
      : `Trading ${status.toLowerCase()}: ${formatReasonLabel(reason)}`
  };

  return {
    status,
    reason,
    color: config.color,
    label: reasonInfo.label,
    description: reasonInfo.description,
    guardAt: bot.guardAt,
    guardPrice: bot.guardPrice,
    guardSide: bot.guardSide,
  };
}

/* ── Scanner grouping ─────────────────────────────────────────────────────── */

/**
 * `{ manual, scannerProfiles, spawnedByProfile (Map profileId → bots),
 *    orphanedSpawned }` — spawned bots whose parent profile no longer exists
 * are swept into `orphanedSpawned`.
 */
export function groupBots(bots = []) {
  const manual = [];
  const scannerProfiles = [];
  const spawnedByProfile = new Map();   // profileId → [bots...]
  const orphanedSpawned = [];           // spawned bots whose parent was deleted

  for (const bot of bots) {
    if (bot.isScannerProfile) {
      scannerProfiles.push(bot);
    } else if (bot.spawnedFromProfileId) {
      const arr = spawnedByProfile.get(bot.spawnedFromProfileId) || [];
      arr.push(bot);
      spawnedByProfile.set(bot.spawnedFromProfileId, arr);
    } else {
      manual.push(bot);
    }
  }

  // Sweep up spawned bots whose parent doesn't exist (deleted scanner)
  for (const [profileId, children] of spawnedByProfile.entries()) {
    if (!scannerProfiles.find(p => p.id === profileId)) {
      orphanedSpawned.push(...children);
      spawnedByProfile.delete(profileId);
    }
  }

  return { manual, scannerProfiles, spawnedByProfile, orphanedSpawned };
}

const botLive = {
  HISTORY_PAGE_SIZE,
  mapHistoryRows,
  historyKeyOf,
  mergeHistoryEntries,
  computeStatsFromHistory,
  WEBHOOK_BASE_URL,
  TV_WEBHOOK_PATH,
  buildTradingViewWebhookUrl,
  normalizeBotStats,
  formatBotRow,
  mergeBotLists,
  socketStatsOf,
  socketHistoryEntries,
  applyBotSocketUpdate,
  formatReasonLabel,
  tradingStatusOf,
  groupBots,
};

export default botLive;
